/**
 * HTTP client shared by the tenant app and the Platform Console. Access token in memory only; refresh token is an
 * httpOnly cookie. A 401 triggers one single-flight refresh and a retry.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(code);
  }
}

export interface ApiClientOptions<S> {
  /** Path prefix the dev proxy / ingress maps to the backend, e.g. "/api" or "/cp". */
  base: string;
  refreshPath: string;
  /** Paths that must never trigger a refresh-and-retry (sign-in itself). */
  noRetry: string[];
  tokenOf: (session: S) => string;
}

export function createApiClient<S>(opts: ApiClientOptions<S>) {
  let accessToken: string | null = null;
  let refreshing: Promise<S | null> | null = null;
  let listener: (s: S | null) => void = () => undefined;

  const refresh = (): Promise<S | null> => {
    refreshing ??= fetch(`${opts.base}${opts.refreshPath}`, { method: 'POST', credentials: 'same-origin' })
      .then(async (r) => (r.ok ? ((await r.json()) as S) : null))
      .catch(() => null)
      .then((s) => {
        accessToken = s ? opts.tokenOf(s) : null;
        listener(s);
        return s;
      })
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  };

  async function api<T>(
    path: string,
    init: RequestInit & { idempotencyKey?: string } = {},
    retry = true,
  ): Promise<T> {
    const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
    if (init.body !== undefined) headers['content-type'] = 'application/json';
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (init.idempotencyKey) headers['idempotency-key'] = init.idempotencyKey;
    const res = await fetch(`${opts.base}${path}`, { ...init, headers, credentials: 'same-origin' });
    if (res.status === 401 && retry && !opts.noRetry.includes(path)) {
      if (await refresh()) return api<T>(path, init, false);
    }
    if (res.status === 204) return undefined as T;
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = (body as { error?: { code?: string; details?: unknown } }).error;
      throw new ApiError(res.status, err?.code ?? 'internal', err?.details);
    }
    return body as T;
  }

  /** Fetches a file (e.g. a CSV export) with the same auth and refresh handling, and saves it in the browser. */
  async function download(path: string, filename: string, retry = true): Promise<void> {
    const headers: Record<string, string> = accessToken ? { authorization: `Bearer ${accessToken}` } : {};
    const res = await fetch(`${opts.base}${path}`, { headers, credentials: 'same-origin' });
    if (res.status === 401 && retry && (await refresh())) return download(path, filename, false);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = (body as { error?: { code?: string } }).error;
      throw new ApiError(res.status, err?.code ?? 'internal');
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }

  /** Fetches a file with the same auth (e.g. a call recording to play in the page). */
  async function blob(path: string, retry = true): Promise<Blob> {
    const headers: Record<string, string> = accessToken ? { authorization: `Bearer ${accessToken}` } : {};
    const res = await fetch(`${opts.base}${path}`, { headers, credentials: 'same-origin' });
    if (res.status === 401 && retry && (await refresh())) return blob(path, false);
    if (!res.ok) throw new ApiError(res.status, 'not_found');
    return res.blob();
  }

  /** Uploads a file as the raw request body (photos and videos for posts). */
  async function upload<T>(path: string, file: Blob, retry = true): Promise<T> {
    const headers: Record<string, string> = { 'content-type': file.type || 'application/octet-stream' };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    const res = await fetch(`${opts.base}${path}`, {
      method: 'POST',
      headers,
      body: file,
      credentials: 'same-origin',
    });
    if (res.status === 401 && retry && (await refresh())) return upload<T>(path, file, false);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = (body as { error?: { code?: string; details?: unknown } }).error;
      throw new ApiError(res.status, err?.code ?? 'internal', err?.details);
    }
    return body as T;
  }

  return {
    api,
    download,
    upload,
    blob,
    refresh,
    setToken: (t: string | null) => {
      accessToken = t;
    },
    onSession: (fn: (s: S | null) => void) => {
      listener = fn;
    },
  };
}
